<template>
  <section
    ref="section4"
    :class="[(Object.keys(validationErrors).length > 0 ? 'has-errors' : ''), 'mx-auto flex h-full flex-col']"
  >
    <v-add-listing-head
      :routes="routes"
      :step="2"
    ></v-add-listing-head>
    <main
      class="flex h-full flex-col items-center justify-between overflow-y-scroll px-4 py-8 md:px-12"
    >
        <figure class="flex flex-col gap-10 lg:w-full lg:gap-11 pb-32">
            <v-error-display :validation-errors="validationErrors"></v-error-display>
            <form method="POST" :action="routes.submitForm" id="select-estate-type-step">
                <v-csrf-token></v-csrf-token>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('subtype')">
                    <figure class="flex gap-3">
                        <div>
                            <v-icon icon="house-work" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Izberite tip vaše nepremičnine
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko samo eno možnost.
                            </div>
                        </div>
                    </figure>
                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-option-dropdown 
                            :options="estateTypeOptions" 
                            name="objekttyp" 
                            label="Tip nepremičnine" 
                            :default-value="objekttypDefaultValue" 
                            ref="estateSubtypeRef" 
                            :on-change="onEstateSubypeChange"
                        />
                    </figure>
                </figure>

                <v-separator 
                    classes="max-w-2xl mx-auto" 
                    v-if="prerequisitesMet('vseljivost') && prerequisitesMet('subtype')"
                />

                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('vseljivost')">
                    <figure class="flex gap-3">
                        
                        <div>
                            <v-icon icon="overview" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Od kdaj bo nepremičnina vseljiva?
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete takoj ali podajte točen datum
                            </div>
                        </div>
                    </figure>

                    <figure class="mx-auto w-full grid grid-cols-1 md:grid-cols-3 grid-rows-1 gap-4 lg:flex lg:grid-cols-2 lg:flex-wrap lg:justify-center lg:gap-6 items-stretch">
                        <div
                            @click="clearDate()"
                            name="takoj"
                            :class="[
                                'tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]',
                                (validationErrors.verfuegbar_ab ? 'has-errors' : '')
                            ]"
                        >
                            <input
                                type="radio"
                                name="verfuegbar_ab"
                                id="kdajBoVasaNepremicninaVseljiva-takoj"
                                value="now"
                                class="hidden"
                                v-model="availability"
                            />

                            <label
                                for="kdajBoVasaNepremicninaVseljiva-takoj"
                                class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-4 md:py-1 text-body-medium-button text-black-78 shadow transition-all h-full"
                            >
                                <v-icon icon="calendar-check" size="5"></v-icon>
                                <span class="pointer-events-none select-none">Takoj</span>
                            </label>
                        </div>

                        <span class="flex items-center justify-center text-black-40 text-small-text-regular">
                            Ali
                        </span>

                        <div :class="[
                            'input group relative flex flex-col gap-[10px] flex-grow md:mt-0',
                            (validationErrors.abdatum ? 'has-errors' : '')
                        ]">
                            <v-datepicker
                                tabindex="-1"
                                ref="datumKdajBoVseljivoRef"
                                v-model="dateFrom"
                                :formatter="dateFormatter"
                                id="datumKdajBoVseljivo"
                                name="abdatum"
                                placeholder="Točen datum"
                                as-single
                            />
                        </div>
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('leased')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('leased')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="location-away" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Ali je nepremičnina trenutno oddana?
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Ali je nepremičnina trenutno oddana?
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Potrebujemo podatek, ali je nepremičnina trenutno oddana. Za zainteresirano stranko je to zelo pomemben podatek.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-option-dropdown 
                            :options="leasedOptions"
                            name="vermietet"
                            label="Ali je oddana?"
                            :default-value="leasedDefaultValue"
                            ref="leasedRef"
                            :on-change="onLeasedChange"
                        />
                    </figure>

                    <ul v-if="showLeasedStatus && leased == 1" class="grid grid-cols-4 flex-col gap-x-5 gap-y-8">
                        <li class="col-span-4">
                            <div :class="[
                                'input group relative flex flex-col gap-[10px]',
                                validationErrors.mieteinnahmen_pro_monat ? 'has-errors' : ''
                            ]">
                                <label for="netoNajemnina" class="w-full text-body-medium-button">
                                    <div class="label flex flex-col gap-[10px]">
                                        <div class="flex items-center gap-3">
                                            <v-icon icon="payments" size="4"></v-icon>
                                            <span>Neto najemnina<sup>*</sup></span>
                                        </div>
                                    </div>
                                </label>
                                <input
                                    id="netoNajemnina"
                                    type="number"
                                    name="mieteinnahmen_pro_monat"
                                    placeholder="Neto najemnina"
                                    v-model="lease"
                                />
                                <div class="absolute bottom-[18px] right-6 text-body-medium-button"
                                    :class="lease ? 'text-black-78' : 'text-black-40'">
                                    €/mesec
                                </div>
                            </div>
                        </li>
                    </ul>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('size')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('size')">
                    <figure class="flex gap-3">
                        <div>
                            <v-icon icon="size" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Velikost nepremičnine
                            </div>
                        </div>
                    </figure>

                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <ul class="grid grid-cols-4 flex-col gap-x-5 gap-y-8">
                            <li class="col-span-4" v-if="['haus', 'zimmer', 'wohnung', 'ind_Schl_3259', 'hausbau'].includes(this.stepData.objektart)">
                                <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.wohnflaeche ? 'has-errors' : '')]">
                                    <label for="bivalnaPovrsina" class="w-full text-body-medium-button">
                                        <div class="label flex flex-col gap-[10px]">
                                            <div class="flex items-center gap-3">
                                                <v-icon icon="square-size" size="4"></v-icon>
                                                <span>Bivalna površina<sup>*</sup></span>
                                            </div>
                                        </div>
                                    </label>
                                    <v-form-input
                                        id="bivalnaPovrsina"
                                        type="decimal"
                                        v-model="livingArea"
                                        :value="livingArea"
                                        name="wohnflaeche"
                                        :placeholder="'Bivalna površina'"
                                        :label="'Bivalna površina'"
                                        :error-message="validationErrors.wohnflaeche ? validationErrors.wohnflaeche[0] : null"
                                    />
                                    <div class="absolute bottom-[18px] right-6 text-body-medium-button" :class="livingArea ? 'text-black-78' : 'text-black-40'">
                                        m<sup>2</sup>
                                    </div>
                                </div>
                            </li>

                            <li class="col-span-4" v-if="['haus', 'hausbau', 'grundstueck'].includes(this.stepData.objektart)">
                                <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.grundstuecksflaeche ? 'has-errors' : '')]">
                                    <label for="povrsinaParcele" class="w-full text-body-medium-button">
                                        <div class="label flex flex-col gap-[10px]">
                                            <div class="flex items-center gap-3">
                                                <v-icon icon="land" size="4"></v-icon>
                                                <span>Površina parcele<sup>*</sup></span>
                                            </div>
                                        </div>
                                    </label>
                                    <v-form-input
                                        id="povrsinaParcele"
                                        type="decimal"
                                        v-model="landArea"
                                        :value="landArea"
                                        name="grundstuecksflaeche"
                                        :placeholder="'Površina parcele'"
                                        :label="'Površina parcele'"
                                        :error-message="validationErrors.grundstuecksflaeche ? validationErrors.grundstuecksflaeche[0] : null"
                                    />
                                    <div class="absolute bottom-[18px] right-6 text-body-medium-button"
                                        :class="landArea ? 'text-black-78' : 'text-black-40'">
                                        m<sup>2</sup>
                                    </div>
                                </div>
                            </li>

                            <li class="col-span-2" v-if="['haus', 'wohnung', 'ind_Schl_3259', 'hausbau'].includes(this.stepData.objektart)">
                                <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.anzahl_zimmer ? 'has-errors' : '')]">
                                    <label for="steviloSob" class="w-full text-body-medium-button">
                                        <div class="label flex flex-col gap-[10px]">
                                            <div class="flex items-center gap-3">
                                                <v-icon icon="dor" size="4"></v-icon>
                                                <span>Število sob<sup>*</sup></span>
                                            </div>
                                        </div>
                                    </label>
                                    <v-form-input
                                        id="steviloSob"
                                        type="number"
                                        v-model="numberOfRooms"
                                        :value="numberOfRooms"
                                        name="anzahl_zimmer"
                                        :placeholder="'Število'"
                                        :error-message="validationErrors.anzahl_zimmer ? validationErrors.anzahl_zimmer[0] : null"
                                    />
                                </div>
                            </li>

                            <li class="col-span-2" v-if="['haus', 'wohnung', 'ind_Schl_3259', 'hausbau'].includes(this.stepData.objektart)">
                                <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.anzahl_schlafzimmer ? 'has-errors' : '')]">
                                    <label for="steviloSpalnic" class="w-full text-body-medium-button">
                                        <div class="label flex flex-col gap-[10px]">
                                            <div class="flex items-center gap-3">
                                                <v-icon icon="bed" size="4"></v-icon>
                                                <span>Število spalnic<sup>*</sup></span>
                                            </div>
                                        </div>
                                    </label>
                                    <v-form-input
                                        id="steviloSpalnic"
                                        type="number"
                                        v-model="numberOfBedrooms"
                                        :value="numberOfBedrooms"
                                        name="anzahl_schlafzimmer"
                                        :placeholder="'Število'"
                                        :error-message="validationErrors.anzahl_schlafzimmer ? validationErrors.anzahl_schlafzimmer[0] : null"
                                    />
                                </div>
                            </li>

                            <li class="col-span-2" v-if="['haus', 'wohnung', 'ind_Schl_3259', 'hausbau'].includes(this.stepData.objektart)">
                                <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.anzahl_badezimmer ? 'has-errors' : '')]">
                                    <label for="steviloKopalnic" class="w-full text-body-medium-button">
                                        <div class="label flex flex-col gap-[10px]">
                                            <div class="flex items-center gap-3">
                                                <v-icon icon="shower"></v-icon>
                                                <span>Število kopalnic<sup>*</sup></span>
                                            </div>
                                        </div>
                                    </label>
                                    <v-form-input
                                        id="steviloKopalnic"
                                        type="number"
                                        v-model="numberOfBathrooms"
                                        :value="numberOfBathrooms"
                                        name="anzahl_badezimmer"
                                        :placeholder="'Število'"
                                        :error-message="validationErrors.anzahl_badezimmer ? validationErrors.anzahl_badezimmer[0] : null"
                                    />
                                </div>
                            </li>

                            <li class="col-span-2" v-if="['haus', 'hausbau'].includes(this.stepData.objektart)">
                                <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.etagen_zahl ? 'has-errors' : '')]">
                                    <label for="steviloNadstropij" class="w-full text-body-medium-button">
                                        <div class="label flex flex-col gap-[10px]">
                                            <div class="flex items-center gap-3">
                                                <v-icon icon="floor"></v-icon>
                                                <span>Število nadstropij<sup>*</sup></span>
                                            </div>
                                        </div>
                                    </label>
                                    <v-form-input
                                        id="steviloNadstropij"
                                        type="number"
                                        v-model="numberOfFloors"
                                        :value="numberOfFloors"
                                        name="etagen_zahl"
                                        :placeholder="'Število'"
                                        :error-message="validationErrors.etagen_zahl ? validationErrors.etagen_zahl[0] : null"
                                    />
                                </div>
                            </li>
                        </ul>
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('floor')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('floor')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="stairs" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                V katerem nadstropju je vaša nepremičnina?
                            </div>
                        </div>
                    </figure>

                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-floor-selection
                            ref="floorPicker"
                            :default-floor="floorDefaultValue"
                            :onFloorChanged="onFloorChanged"
                            :routes="routes"
                            :validation-errors="validationErrors"
                        ></v-floor-selection>
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('status-gradnje')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('status-gradnje')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="overview" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Status gradnje nepremičnine
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">Zgrajeno</p>
                                <p class="mb-8 text-body-regular">
                                    To možnost izberite, če se gre za že zgrajeno nepremičnino z uporabnim dovoljenjem.
                                </p>
                                <p class="mb-[10px] text-icon-text-medium">V gradnji ali v načrtovanju</p>
                                <p class="mb-4 text-body-regular">
                                    Kasneje pri opisih nepremičnine je v prostem besedilu potreben tudi natančen opis, v kateri fazi je gradnja nepremičnine.
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Pri letu gradnje vnesite leto v katerem je izgradnja nepremičnine z uporabniškim dovoljenjem načrtovana.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <figure 
                            v-if="showConstructionStatus" 
                            class="grid grid-cols-1 grid-rows-1 gap-4 lg:flex lg:grid-cols-2 lg:flex-wrap lg:justify-center lg:gap-4"
                        >
                            <div 
                                name="zgrajeno" 
                                :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', (validationErrors.bauphase ? 'has-errors' : '')]"
                            >
                                <input
                                    type="radio"
                                    name="bauphase"
                                    id="status-gradnje-1"
                                    value="HausFertigGestellt"
                                    class="hidden"
                                    v-model="constructionPhase"
                                />
                                <label
                                    for="status-gradnje-1"
                                    class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all"
                                >
                                    <v-icon icon="circle-check" size="5"></v-icon>
                                    <span class="pointer-events-none select-none">Zgrajeno</span>
                                </label>
                            </div>

                            <div name="v-gradnji" :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', (validationErrors.bauphase ? 'has-errors' : '')]" v-if="(this.stepData.objektart == 'hausbau' || this.stepData.objektart == 'ind_Schl_3259')">
                                <input 
                                    type="radio" 
                                    name="bauphase" 
                                    id="status-gradnje-2" 
                                    value="HausImBau" 
                                    class="hidden" 
                                    v-model="constructionPhase" 
                                />
                                <label 
                                    for="status-gradnje-2" 
                                    class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all"
                                >
                                    <v-icon icon="engineering" size="5"></v-icon>
                                    <span class="pointer-events-none select-none">V gradnji</span>
                                </label>
                            </div>

                            <div 
                                name="v-nacrtovanju" 
                                :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', (validationErrors.bauphase ? 'has-errors' : '')]" 
                                v-if="(this.stepData.objektart == 'hausbau' || this.stepData.objektart == 'ind_Schl_3259')"
                            >
                                <input 
                                    type="radio" 
                                    name="bauphase" 
                                    id="status-gradnje-3" 
                                    value="HausInPlanung" 
                                    class="hidden" 
                                    v-model="constructionPhase" 
                                />
                                <label for="status-gradnje-3" class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all">
                                    <v-icon icon="design-services" size="5"></v-icon>
                                    <span class="pointer-events-none select-none">V načrtovanju</span>
                                </label>
                            </div>
                        </figure>

                        <figure 
                            class="ml-auto mr-auto w-full mt-6" 
                            v-if="showConstructionStatus && constructionPhase == 'HausFertigGestellt'"
                        >
                            <ul class="grid grid-cols-4 flex-col gap-x-5 gap-y-8">
                                <li class="col-span-4">
                                    <div :class="['input group relative flex flex-col gap-[10px]', (validationErrors.baujahr ? 'has-errors' : '')]">
                                        <label for="letoIzgradje" class="w-full text-body-medium-button">
                                            <div class="label flex flex-col gap-[10px]">
                                                <div class="flex items-center gap-3">
                                                    <v-icon icon="calendar-dot-mono" size="4"></v-icon>
                                                    <span>Leto izgradnje<sup>*</sup></span>
                                                </div>
                                            </div>
                                        </label>
                                        <v-form-input 
                                            type="text" 
                                            id="letoIzgradje" 
                                            name="baujahr" 
                                            placeholder="Leto izgradnje" 
                                            label="Leto izgradnje" 
                                            v-model="constructionYear" 
                                            :value="constructionYear" 
                                            :error-message="validationErrors.baujahr ? validationErrors.baujahr[0] : null" 
                                            is-year-input 
                                        />
                                    </div>
                                </li>
                            </ul>
                        </figure>
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('opremljenost')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('opremljenost')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="chair" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Kako je opremljena nepremičnina?
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko samo eno možnost
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Opremljenost nepremičnine
                                </p>
                                <p class="text-body-regular">
                                    Med možnostmi izberite, ali je vaša nepremičnina:
                                </p>
                                <ul class="mb-8 text-body-regular list-disc pl-6">
                                    <li>neopremljena (stanovanje nima pohištva)</li>
                                    <li>delno opremljena (stanovanje ima le del pohištva, vendar bo kupec potreboval dokupiti pohištvo za celostno opremljeno nepremičnino)</li>
                                    <li>opremljeno (stanovanje je polno opremljeno in primerno za takojšnje bivanje)</li>
                                </ul>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Delno opremljeno ali opremljeno
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Če izberete "delno opremljeno" ali "opremljeno", vas prosimo, da kasneje pri opish nepremičnine v prostem besedilu opišete, kako je stanovanje opremljeno, katero pohištvo je v nepremičnini in v kakšnem stanju je. Tako zainteresirana stranka dobi boljši pregled, kaj bi še potrebovala.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>
                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-option-dropdown 
                            :options="estateEquipmentOptions" 
                            name="ind_2028_Feld_ObjAusstattung39" 
                            label="Opremljenost" 
                            :default-value="estateEquipmentDefaultValue" 
                            :on-change="onEstateEquipmentChange"
                        />
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('renovations')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('renovations')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="build-circle" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Je bila nepremičnina prenovljena?
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Prenova nepremičnine
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Vnesite ali je bila vaša nepremičnina v zadnjih časih (obdobje približno 5. letih) prenovljena ali ne. Pri vsaki možnosti prenov so tudi opisi, ki podrobneje razložijo pomen vsakega tipa prenove.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-option-dropdown 
                            :options="isRenovatedOptions" 
                            name="renovation_status" 
                            label="Ali je obnovljena?" 
                            :default-value="isRenovatedDefaultValue"
                            :on-change="onIsRenovatedChange"
                        />
                    </figure>

                    <div class="flex flex-col gap-2">
                        <figure v-if="showRenovations && renovated != null" class="mb-4 mt-8 flex w-full items-center justify-center gap-[10px] text-body-medium-button text-black-78">
                            <v-icon icon="house-work"></v-icon>
                            <span>Trenutno stanje nepremičnine <sup>*</sup></span>
                        </figure>

                        <figure class="mx-auto w-full" v-if="showRenovations && renovated !== null">
                            <v-option-dropdown 
                                v-if="renovated == 1" 
                                :options="renovatedYesOptions" 
                                name="zustand" 
                                label="Trenutno stanje" 
                                :default-value="status"
                                :on-change="onRenovatedYesNoChange"
                            />
                            <v-option-dropdown 
                                v-if="renovated == 0" 
                                :options="renovatedNoOptions" 
                                name="zustand" 
                                label="Trenutno stanje" 
                                :default-value="status"
                                :on-change="onRenovatedYesNoChange"
                            />
                        </figure>
                    </div>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('renovated-what')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('renovated-what')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="home-repair-service" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Kaj je bilo prenovljeno
                            </div>
                            <div class="text-body-regular text-black-40">
                                Vnesite podrobne podatke o prenovi nepremičnine.
                                Te podatki so zelo pomembni za izboljšanje vaše
                                ponujene cene.
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Prenove na nepremičnini
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Tukaj vnesite podatke prenov vaše nepremičnine. Te podatki so pomembni za kupca ali najemnika, saj lahko samo tako približno ocenijo morebitne dodatne stroške. Pri letu zadnje prenove vnesite samo leto, kdaj je bilo na zadnje eno od opisanih del izvedeno. V kolikor nimate podatkov, pustite polja prazna.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <ul class="grid grid-cols-4 flex-col gap-x-5 gap-y-4 w-full mx-auto">
                        <li class="col-span-4">
                            <div :class="['input group relative flex flex-col gap-[10px]', validationErrors.last_renovation_year ? 'has-errors' : '']">
                                <label for="letoZadnjePrenove" class="w-full text-body-medium-button">
                                    <div class="label flex flex-col gap-[10px]">
                                        <div class="flex items-center gap-3">
                                            <v-icon icon="handyman" size="4"></v-icon>
                                            <span>Leto zadnje prenove<sup>*</sup></span>
                                        </div>
                                    </div>
                                </label>
                                <v-form-input
                                    type="text"
                                    id="letoZadnjePrenove"
                                    name="last_renovation_year"
                                    placeholder="Leto"
                                    label="Leto"
                                    v-model="lastRenovationYear"
                                    :value="lastRenovationYear"
                                    :error-message="validationErrors.last_renovation_year ? validationErrors.last_renovation_year[0] : null"
                                    is-year-input
                                />
                            </div>
                        </li>

                        <li class="col-span-4 mt-4">
                            <v-checkbox
                                label="Prenova oken"
                                checkbox-position="right"
                                name="prenovaOken"
                                :checked="Boolean(windowsRenovationCasted)"
                                icon="window"
                                @vchanged="windowsRenovation = !windowsRenovation"
                                :class="['checkbox group flex items-center', validationErrors.renovation_window ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>

                        <li class="col-span-4">
                            <v-checkbox
                                label="Prenova strehe"
                                checkbox-position="right"
                                name="prenovaStrehe"
                                :checked="Boolean(roofRenovationCasted)"
                                icon="roofing"
                                @vchanged="roofRenovation = !roofRenovation"
                                :class="['checkbox group flex items-center', validationErrors.renovation_floor ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>

                        <li class="col-span-4">
                            <v-checkbox
                                label="Prenova tal"
                                checkbox-position="right"
                                name="prenovaTal"
                                :checked="Boolean(floorsRenovationCasted)"
                                icon="stairs"
                                @vchanged="floorsRenovation = !floorsRenovation"
                                :class="['checkbox group flex items-center', validationErrors.renovation_roof ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>

                        <li class="col-span-4">
                            <v-checkbox
                                label="Nove barve sten"
                                checkbox-position="right"
                                name="noveBarveSten"
                                :checked="Boolean(wallsRenovationCasted)"
                                icon="roller"
                                @vchanged="wallsRenovation = !wallsRenovation"
                                :class="['checkbox group flex items-center', validationErrors.renovation_walls ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>

                        <li class="col-span-4">
                            <v-checkbox
                                label="Prenova ogrevanja"
                                checkbox-position="right"
                                name="prenovaOgrevanja"
                                :checked="Boolean(heatingRenovationCasted)"
                                icon="water-heater"
                                @vchanged="heatingRenovation = !heatingRenovation"
                                :class="['checkbox group flex items-center', validationErrors.renovation_heating ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>

                        <li class="col-span-4">
                            <v-checkbox
                                label="Prenova vodne inštalacije"
                                checkbox-position="right"
                                name="prenovaVodneInstalacije"
                                :checked="Boolean(plumbingRenovationCasted)"
                                icon="faucet"
                                @vchanged="plumbingRenovation = !plumbingRenovation"
                                :class="['checkbox group flex items-center', validationErrors.renovation_pipeing ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>

                        <li class="col-span-4">
                            <v-checkbox
                                label="Prenova električne instalacije"
                                checkbox-position="right"
                                name="prenovaElektricneInstalacije"
                                :checked="Boolean(electricalRenovationsCasted)"
                                icon="electric-meter"
                                @vchanged="electricalRenovations = !electricalRenovations"
                                :class="['checkbox group flex items-center', validationErrors.renovation_electrical ? 'has-errors' : '']"
                            ></v-checkbox>
                        </li>
                    </ul>

                    <div class="hidden-inputs hidden">
                        <input type="text" name="renovation_window" class="hidden" v-model="windowsRenovationCasted" />
                        <input type="text" name="renovation_floor" class="hidden" v-model="floorsRenovationCasted" />
                        <input type="text" name="renovation_roof" class="hidden" v-model="roofRenovationCasted" />
                        <input type="text" name="renovation_walls" class="hidden" v-model="wallsRenovationCasted" />
                        <input type="text" name="renovation_heating" class="hidden" v-model="heatingRenovationCasted" />
                        <input type="text" name="renovation_pipeing" class="hidden" v-model="plumbingRenovationCasted" />
                        <input type="text" name="renovation_electrical" class="hidden" v-model="electricalRenovationsCasted" />
                    </div>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('utility-connections')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('utility-connections')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="water-pump" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Komunalni priključki
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberite kako je vaša parcela priključena na komunalne priključke:
                            </div>
                        </div>
                    </figure>
                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-option-dropdown
                            :options="utilityConnectionOptions"
                            name="erschliessung"
                            label="Priključenost"
                            :default-value="stepData.erschliessung"
                            :on-change="onUtilityConnectionChange"
                        />
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('buildability')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('buildability')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="contract-edit" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Zazidljivost parcele
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko samo eno možnost.
                            </div>
                        </div>
                    </figure>
                    <figure class="ml-auto mr-auto flex w-full flex-col gap-8">
                        <v-option-dropdown 
                            :options="buildabilityOptions"
                            name="bebaubar_nach"
                            label="Zazidljivost"
                            :default-value="stepData.bebaubar_nach"
                            :on-change="onBuildabilityChange"
                        />
                    </figure>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('extra-data')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('extra-data')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="home-repair-service" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Dodatni podatki
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko več možnosti
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Spomeniško varstvo
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Preverite, če imate potrejeno spomeniško varstvo na vašem objektu. V primeru, da je nepremičnina spomeniško zaščitena, odkljukajte to možnost.
                                </p>
                                <p class="text-body-regular">
                                    Objekti pod spomeniškim varstvom so običajno zaradi njihove starosti ali državne ali mednarodne kulturne pomembnosti, zaščiteni. Prenove ali kakršnekoli spremembe na tako imenovanih objektih se mora ob morajo uskladiti s pravnimi organi.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <ul class="grid grid-cols-1 md:grid-cols-2 flex-col gap-x-6 gap-y-4">
                        <li v-for="(extra, onOfficeEnum) in stepData.extra_data_options" :key="'extra-' + onOfficeEnum">
                            <v-checkbox
                                :id="onOfficeEnum"
                                :name="'extra_data[' + onOfficeEnum + ']'"
                                :label="stepData.labels[onOfficeEnum].label"
                                checkbox-position="right"
                                :value="onOfficeEnum"
                                :icon="extra.icon"
                                :checked="extraDataSelected(onOfficeEnum)"
                            ></v-checkbox>
                        </li>
                    </ul>

                    <v-separator classes="max-w-2xl mx-auto w-full" spacing="tight"/>

                    <ul class="grid flex-col gap-x-6 gap-y-4">
                        <li>
                            <v-checkbox
                                name="aliNepremicninaSpadaPodSpomeniskoVarstvo"
                                label="Ali nepremičnina spada pod spomeniško varstvo"
                                checkbox-position="right"
                                icon="shield"
                                @vchanged="isMonument = !isMonument"
                                :checked="Boolean(isMonumentCasted)"
                            ></v-checkbox>
                        </li>
                    </ul>

                    <input type="hidden" name="denkmalgeschuetzt" v-model="isMonumentCasted"/>

                    <div class="mt-4">
                        <figure v-if="stepData.showpets" class="mb-4 flex w-full items-center gap-[10px] text-body-medium-button text-black-78 lg:justify-center">
                            <v-icon icon="pets" size="4"></v-icon>
                            <span>Ali so hišni ljubljenčki dovoljeni? <sup>*</sup></span>
                        </figure>

                        <figure v-if="stepData.showpets" class="grid grid-cols-1 grid-rows-1 gap-4 lg:grid-cols-3 lg:justify-center lg:gap-6">
                            <div 
                                name="aliSohisniLjubljenckiDovoljeni-da" 
                                :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', validationErrors.haustiere ? 'has-errors' : '']"
                            >
                                <input 
                                    type="radio" 
                                    name="haustiere" 
                                    id="aliSohisniLjubljenckiDovoljeni-da" 
                                    value="1" 
                                    class="hidden" 
                                    v-model="allowsPets"
                                />
                                <label 
                                    for="aliSohisniLjubljenckiDovoljeni-da" 
                                    class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all"
                                >
                                    <v-icon icon="circle-check" size="5"></v-icon>
                                    <span class="pointer-events-none select-none">Da</span>
                                </label>
                            </div>

                            <div 
                                name="aliSohisniLjubljenckiDovoljeni-ne" 
                                :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', validationErrors.haustiere ? 'has-errors' : '']"
                            >
                                <input 
                                    type="radio" 
                                    name="haustiere" 
                                    id="aliSohisniLjubljenckiDovoljeni-ne" 
                                    value="0" 
                                    class="hidden" 
                                    v-model="allowsPets"
                                />
                                <label 
                                    for="aliSohisniLjubljenckiDovoljeni-ne" 
                                    class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all"
                                >
                                    <v-icon icon="cancel" size="5"></v-icon>
                                    <span class="pointer-events-none select-none">Ne</span>
                                </label>
                            </div>

                            <div 
                                name="aliSohisniLjubljenckiDovoljeni-poDogovoru" 
                                :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', validationErrors.haustiere ? 'has-errors' : '']"
                            >
                                <input 
                                    type="radio" 
                                    name="haustiere" 
                                    id="aliSohisniLjubljenckiDovoljeni-poDogovoru" 
                                    value="2" 
                                    class="hidden" 
                                    v-model="allowsPets"
                                />
                                <label 
                                    for="aliSohisniLjubljenckiDovoljeni-poDogovoru" 
                                    class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all"
                                >
                                    <v-icon icon="voice" size="5"></v-icon>
                                    <span class="pointer-events-none select-none">Po dogovoru</span>
                                </label>
                            </div>
                        </figure>
                    </div>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('extra-data')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('extra-data')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="car" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Parkirna mesta
                            </div>
                            <div class="text-body-regular text-black-40">
                                Označite katere vrste parkirnih mest ima nepremičnina in vnesite število parkirnih mest. Lahko izberete več možnosti.
                            </div>
                        </div>
                    </figure>

                    <ul class="grid grid-cols-1 md:grid-cols-2 flex-col gap-x-6 gap-y-4">
                        <li v-for="(garage, onOfficeEnum) in stepData.stellplatzart_options" 
                            :key="'garage-' + onOfficeEnum">
                            <v-checkbox
                                :id="onOfficeEnum"
                                class="text-small-text-medium text-black-78"
                                :label="stepData.labels.stellplatzart.values[onOfficeEnum]"
                                checkbox-position="right"
                                :name="'stellplatzart[' + onOfficeEnum + ']'"
                                :value="onOfficeEnum"
                                :icon="garage.icon"
                                :checked="parkingSelected(onOfficeEnum)"
                            ></v-checkbox>
                        </li>
                    </ul>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('heating')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('heating')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="fire" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Kateri je glavni vir ogrevanja
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko več možnosti
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Vir ogrevanja
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Vir ogrevanja pomeni s katero energijo se greje vaša nepremičnina. Tukaj lahko izberete več možnosti, v primeru da se lahko nepremičnina ogreva na več različnih virov (npr. nepremičnina se lahko ogreva na olje in hkrati pelete).
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <ul class="grid grid-cols-1 md:grid-cols-2 flex-col gap-x-6 gap-y-4">
                        <li v-for="(heatingSource, onOfficeEnum) in stepData.befeuerung_options" :key="'heating-source-' + onOfficeEnum">
                            <v-checkbox
                                :id="onOfficeEnum"
                                :label="stepData.labels.befeuerung.values[onOfficeEnum]"
                                checkbox-position="right"
                                :name="'befeuerung[' + onOfficeEnum + ']'"
                                :value="onOfficeEnum"
                                :icon="heatingSource.icon"
                                :checked="mainHeatingSelected(onOfficeEnum)"
                                :class="['checkbox group flex items-center', validationErrors.befeuerung ? 'has-errors' : '']"
                                @vchanged="checkboxChanged"
                            ></v-checkbox>
                        </li>
                    </ul>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('heating')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('heating')">
                    <figure class="flex gap-3 relative">
                        <div><v-icon icon="fireplace" size="8"></v-icon></div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Katera je bistvena vrsta ogrevanja?
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko več možnosti
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div
                                    class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25"
                                >
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon
                                        icon="question-mark"
                                        class="mr-2.5"
                                        size="4.5"
                                    ></v-icon>
                                    <div>
                                        Dodatne informacije
                                    </div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Vrsta ogrevanja
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Pri opisu vrste ogrevanja je bistven način, kako se prostori ogrevajo in ne energenti, ki se uporabljajo za ogrevanje.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Etažno ogrevanje
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Pomeni, da ima nepremičnina na vsaki posamezni etaži napravo za ogrevanje.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Ogrevanje s pečjo
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Pomeni, da se nepremičnina ogreva s pečjo (npr. les ali elektrika).
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Centralno ogrevanje
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Center centralnega ogrevanja je peč, ki je običajno v kurilnici. S cevmi je povezana z radiatorji v hiši ali stanovanju, neodvisno od bistvenega vira energije.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Talno ogrevanje
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Je površinsko ogrevanje, ki ogreva prostore s pomočjo cevi, položenih v tla, neodvisno od bistvenega vira energije.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Daljinsko ogrevanje
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Je način ogrevanja stavb, pri katerem toploto prenašamo od večjega vira toplote k porabnikom po cevnem omrežju.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>
                    <ul class="grid grid-cols-1 md:grid-cols-2 flex-col gap-x-6 gap-y-4">
                        <li v-for="(heatingType, onOfficeEnum) in stepData.heizungsart_options" 
                            :key="'heating-type-' + onOfficeEnum">
                            <v-checkbox
                                checkbox-position="right"
                                :id="onOfficeEnum"
                                :name="'heizungsart[' + onOfficeEnum + ']'"
                                :value="onOfficeEnum"
                                :icon="heatingType.icon"
                                :checked="bistvenoOgrevanjeSelected(onOfficeEnum)"
                                :label="stepData.labels.heizungsart.values[onOfficeEnum]"
                                :class="[
                                    'checkbox group flex items-center',
                                    validationErrors.heizungsart ? 'has-errors' : ''
                                ]"
                            ></v-checkbox>
                        </li>
                    </ul>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('heating')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('heating')">
                    <figure class="flex gap-3 relative">
                        <div>
                            <v-icon icon="fireplace" size="8"></v-icon>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Ali ima nepremičnina energetsko izkaznico?
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25">
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon icon="question-mark" class="mr-2.5" size="4.5"></v-icon>
                                    <div>Dodatne informacije</div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Energetska izkaznica
                                </p>
                                <p class="text-body-regular">
                                    Pri obstoječih objektih je energetska izkaznica po zakonu obvezna. V kolikor energetske izkaznice nimate, se pozanimajte če ta že obstaja ali naročite novo. V kolikor jo boste dobili kasneje, kliknite na "Predložena ob ogledu"
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>

                    <figure class="grid grid-cols-1 grid-rows-1 gap-4 lg:grid-cols-3 lg:justify-center lg:gap-4">
                        <div 
                            name="aliImaNepremicninaEnergetskoIzkaznico" 
                            :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', validationErrors.energieausweistyp ? 'has-errors' : '']"
                        >
                            <input 
                                type="radio" 
                                name="energieausweistyp" 
                                id="aliImaNepremicninaEnergetskoIzkaznicoDa" 
                                value="Endenergiebedarf" 
                                class="hidden" 
                                v-model="energyCertificateType" 
                            />
                            <label 
                                for="aliImaNepremicninaEnergetskoIzkaznicoDa" 
                                class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all"
                            >
                                <v-icon icon="circle-check" size="5"></v-icon>
                                <span class="pointer-events-none select-none">Da</span>
                            </label>
                        </div>

                        <div 
                            name="aliImaNepremicninaEnergetskoIzkaznicoPredlozenaObOgledu" 
                            :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', validationErrors.energieausweistyp ? 'has-errors' : '']"
                        >
                            <input 
                                type="radio" 
                                name="energieausweistyp" 
                                id="aliImaNepremicninaEnergetskoIzkaznicoPredlozenaObOgledu" 
                                value="liegtZurBesichtigungVor" 
                                class="hidden" 
                                v-model="energyCertificateType" 
                            />
                            <label 
                                for="aliImaNepremicninaEnergetskoIzkaznicoPredlozenaObOgledu" 
                                class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all lg:h-[68px] lg:py-0"
                            >
                                <v-icon icon="find-in-page" size="5"></v-icon>
                                <span class="pointer-events-none select-none lg:flex-1">Predložena ob ogledu</span>
                            </label>
                        </div>

                        <div 
                            name="aliImaNepremicninaEnergetskoIzkaznicoObjektNePotrebuje" 
                            :class="['tile group relative w-full flex-col sm:flex-row lg:max-w-[171px]', validationErrors.energieausweistyp ? 'has-errors' : '']"
                        >
                            <input 
                                type="radio" 
                                name="energieausweistyp" 
                                id="aliImaNepremicninaEnergetskoIzkaznicoObjektNePotrebuje" 
                                value="es besteht keine Pflicht!" 
                                class="hidden" 
                                v-model="energyCertificateType" 
                            />
                            <label 
                                for="aliImaNepremicninaEnergetskoIzkaznicoObjektNePotrebuje" 
                                class="flex cursor-pointer items-center justify-center gap-[10px] rounded-3xl bg-white p-4 py-6 text-body-medium-button text-black-78 shadow transition-all lg:h-[68px] lg:py-0"
                            >
                                <v-icon icon="cancel" size="5"></v-icon>
                                <span class="pointer-events-none select-none lg:flex-1">Objekt ne potrebuje</span>
                            </label>
                        </div>
                    </figure>

                    <template v-if="energyCertificateType == 'Endenergiebedarf'">
                        <figure class="ml-auto mr-auto w-full">
                            <ul class="grid grid-cols-4 flex-col gap-x-5 gap-y-8">
                                <li class="col-span-2">
                                    <div :class="['input group relative flex flex-col gap-[10px]', validationErrors.energieausweis_gueltig_bis ? 'has-errors' : '']">
                                        <label for="energetskaIzkaznicaVeljavnaDo" class="w-full text-body-medium-button">
                                            <div class="label flex flex-col gap-[10px]">
                                                <div class="flex items-center gap-3">
                                                    <v-icon icon="calendar-check" size="4"></v-icon>
                                                    <span>Veljavna do<sup>*</sup></span>
                                                </div>
                                            </div>
                                        </label>
                                        <v-datepicker 
                                            v-model="energyCertificateValidUntil" 
                                            :formatter="dateFormatter" 
                                            id="datumKdajBoVseljivo" 
                                            name="energieausweis_gueltig_bis" 
                                            placeholder="Datum" 
                                            as-single 
                                        />
                                    </div>
                                </li>
                                <li class="col-span-2">
                                    <div :class="['input group relative flex flex-col gap-[10px]', validationErrors.energieverbrauchskennwert ? 'has-errors' : '']">
                                        <label for="poraba" class="w-full text-body-medium-button">
                                            <div class="label flex flex-col gap-[10px]">
                                                <div class="flex items-center gap-3">
                                                    <v-icon icon="gas-meter" size="4"></v-icon>
                                                    <span>Poraba<sup>*</sup></span>
                                                </div>
                                            </div>
                                        </label>
                                        <!-- <input 
                                            id="poraba" 
                                            type="number" 
                                            name="energieverbrauchskennwert" 
                                            placeholder="0" 
                                            v-model="energyConsumption" 
                                        />-->
                                        <v-form-input
                                            type="number"
                                            name="energieverbrauchskennwert"
                                            placeholder="0"
                                            v-model="energyConsumption"
                                            :value="energyConsumption"
                                            :placeholder="'Število'"
                                            :error-message="validationErrors.energieverbrauchskennwert ? validationErrors.energieverbrauchskennwert[0] : null"
                                        />
                                        <span class="absolute bottom-[18px] right-6 text-body-medium-button" :class="energyConsumption ? 'text-black-78' : 'text-black-40'">kWh/m<sup>2</sup>a</span>
                                    </div>
                                </li>
                            </ul>
                        </figure>

                        <figure class="ml-auto mr-auto w-full">
                            <div :class="['energy-efficiency group relative', validationErrors.energyClass ? 'has-errors' : '']">
                                <label for="energyEfficiency" class="mb-4 block w-full text-body-medium-button">
                                    <div class="label flex flex-col gap-[10px]">
                                        <div class="flex items-center gap-3">
                                            <v-icon icon="energy-leaf" size="4"></v-icon>
                                            <span>Razred energetske učinkovitosti<sup>*</sup></span>
                                        </div>
                                    </div>
                                </label>
                                <span class="mb-2 flex items-center justify-between px-2.5">
                                    <span class="text-sm text-gray-500">A1</span>
                                    <span class="text-sm text-gray-500">A2</span>
                                    <span class="text-sm text-gray-500">B1</span>
                                    <span class="text-sm text-gray-500">B2</span>
                                    <span class="text-sm text-gray-500">C</span>
                                    <span class="text-sm text-gray-500">D</span>
                                    <span class="text-sm text-gray-500">E</span>
                                    <span class="text-sm text-gray-500">F</span>
                                    <span class="text-sm text-gray-500">G</span>
                                </span>
                                <input 
                                    id="energyEfficiency" 
                                    type="range" 
                                    min="1" 
                                    max="9" 
                                    step="1" 
                                    class="h-8 w-full cursor-pointer appearance-none rounded-full bg-gray-200 dark:bg-gray-700" 
                                    v-model="selectedEnergyClass" 
                                />
                                <input name="energyClass" v-model="energyClass" type="hidden" />
                            </div>
                        </figure>

                        <figure class="ml-auto mr-auto w-full">
                            <v-checkbox 
                                icon="water-voc" 
                                class="text-small-text-medium text-black-78" 
                                label="Topla voda vključena" 
                                checkbox-position="right" 
                                name="warmWater" 
                                :checked="Boolean(warmWaterCasted)" 
                                @vchanged="hotWaterIncludedInCalculations = !hotWaterIncludedInCalculations"
                            ></v-checkbox>
                            <input type="text" name="warmwasserEnthalten" class="hidden" v-model="warmWaterCasted" />
                        </figure>
                    </template>
                </figure>
                <v-separator classes="max-w-2xl mx-auto" v-if="prerequisitesMet('documentation')"/>
                <figure class="mx-auto max-w-xl flex flex-col gap-8" v-if="prerequisitesMet('documentation')">
                    <figure class="flex gap-3 relative">
                        <div><v-icon icon="fireplace" size="8"></v-icon></div>
                        <div class="flex flex-col gap-3">
                            <div class="text-heading-3-desktop">
                                Katero dokumentacijo ima nepremičnina?
                            </div>
                            <div class="text-body-regular text-black-40">
                                Izberete lahko več možnosti
                            </div>
                        </div>
                        <v-lightbox is-authenticated footerless>
                            <template v-slot:trigger>
                                <div
                                    class="absolute right-0 lg:-right-32 top-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white shadow hover:bg-green-25"
                                >
                                    <v-icon icon="question-mark" size="4"></v-icon>
                                </div>
                            </template>
                            <template v-slot:head>
                                <div class="flex items-start">
                                    <v-icon
                                        icon="question-mark"
                                        class="mr-2.5"
                                        size="4.5"
                                    ></v-icon>
                                    <div>
                                        Dodatne informacije
                                    </div>
                                </div>
                            </template>
                            <template v-slot:content>
                                <p class="mb-[10px] text-icon-text-medium">
                                    Gradbeno dovoljenje
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Gradbeno dovoljenje je uradni dokument, ki ga izda pristojna občinska uprava ali gradbeni organ in ki dovoljuje izvajanje določenih gradbenih dejavnosti na določenem območju. To dovoljenje je potrebno pred začetkom gradnje novega objekta, rekonstrukcije ali spremembe obstoječega objekta.
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Pridobivanje gradbenega dovoljenja je ključnega pomena, saj zagotavlja, da so gradbena dela skladna z lokalnimi gradbenimi predpisi, prostorskimi načrti in drugimi predpisi. V postopku pridobivanja gradbenega dovoljenja se preveri, ali načrti ustrezajo zemljiščnim predpisom, varnostnim zahtevam, okoljskim standardom in drugim veljavnim predpisom.
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Brez ustreznega gradbenega dovoljenja se gradbena dela ne smejo začeti, in neupoštevanje tega zahteva lahko privede do sankcij, vključno s prenehanjem gradbenih del, globami ali drugimi pravnimi posledicami. V praksi gradbeno dovoljenje ščiti tako lastnike nepremičnine kot tudi širšo skupnost, saj zagotavlja, da se gradbena dela izvajajo v skladu z določenimi standardi in pravili.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Uporabniško dovoljenje
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Uporabno dovoljenje je uradni dokument, ki ga izda pristojna občinska uprava ali gradbeni organ po končani gradnji ali rekonstrukciji objekta. To dovoljenje potrjuje, da je zgrajeni objekt zgrajen v skladu s pogoji gradbenega dovoljenja in da izpolnjuje vse potrebne tehnične, varnostne, higienske in druge predpise za varno bivanje ali uporabo.
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Uporabno dovoljenje se pridobi po zaključku gradbenih del, preden se objekt začne uporabljati. To dovoljenje pomeni, da so bila vsa gradbena dela izvedena v skladu z načrti in predpisi ter da objekt izpolnjuje vse potrebne zahteve za varno in zakonito uporabo.
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Brez veljavnega uporabnega dovoljenja lahko lastniki ali uporabniki objekta naletijo na težave, kot so denarne kazni ali celo prepoved uporabe objekta. Uporabno dovoljenje je torej pomemben dokument, ki potrjuje zakonitost in skladnost zgradbe ter zagotavlja, da je varna in primerna za uporabo v skladu z namenom, za katerega je bila zgrajena.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Zemljiškoknjižni izpisek
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Zemljiškoknjižni izpisek je uradni dokument, ki vsebuje informacije o lastništvu in drugih pravnih dejstvih v zvezi z nepremičnino. To je izpis iz zemljiške knjige, ki je uradna javna evidenca o lastništvu nepremičnin.
                                </p>
                                <p class="text-body-regular">
                                    Med možnostmi izberite, ali je vaša nepremičnina:
                                </p>
                                <ul class="mb-4 text-body-regular list-disc pl-6">
                                    <li>
                                        Podatki o nepremičnini: Vključujejo natančen opis nepremičnine, njeno lokacijo, parcelno številko in podobne tehnične informacije.
                                    </li>
                                    <li>
                                        Lastniški podatki: Navaja trenutnega lastnika nepremičnine in morebitne druge imetnike pravic na njej. Prav tako lahko prikaže pretekle lastniške spremembe.
                                    </li>
                                    <li>
                                        Pravice in bremena: Opozarja na morebitne hipoteke, služnosti, stavbne pravice in druge omejitve ali obremenitve na nepremičnini.
                                    </li>
                                    <li>
                                        Podatki o vpisu: Vključujejo datum vpisa podatkov in morebitne spremembe, ki so se zgodile v zvezi z nepremičnino.
                                    </li>
                                </ul>
                                <p class="mb-8 text-body-regular">
                                    Zemljiški izpisek je pomemben dokument, ko se obravnavajo pravna vprašanja ali transakcije v zvezi z nepremičnino, saj zagotavlja transparentnost in zanesljive informacije o njenem statusu. Pred nakupom nepremičnine je priporočljivo pridobiti zemljiško knjižni izpisek, da se preveri njeno pravno stanje in lastniško strukturo.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Tloris
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Tloris nepremičnine predstavlja načrt ali risbo, ki prikazuje horizontalni pogled na razporeditev prostorov znotraj določene nepremičnine. Tloris je običajno izrisan na ravnem, dvodimenzionalnem platnu in prikazuje razporeditev sob, hodnikov, kuhinje, kopalnice, spalnic in drugih prostorov v stavbi ali stanovanju. Ta načrt omogoča pregled nad razporeditvijo prostorov in merami ter služi kot osnova za načrtovanje in razumevanje prostorske strukture.
                                </p>
                                <p class="text-body-regular">
                                    Elementi, ki so običajno vključeni v tloris, so:
                                </p>
                                <ul class="mb-4 text-body-regular list-disc pl-6">
                                    <li>
                                        Stene: Prikazujejo lokacijo sten in njihovo razporeditev v prostoru.
                                    </li>
                                    <li>
                                        Okna in vrata: Označujejo odprtine za okna in vrata ter njihovo lokacijo.
                                    </li>
                                    <li>
                                        Prostori: Različni prostori so lahko označeni, na primer kuhinja, spalnica, dnevna soba, kopalnica, hodnik itd.
                                    </li>
                                    <li>
                                        Mere: Podaja dimenzije prostorov in razdalje med stenami.
                                    </li>
                                </ul>
                                <p class="mb-8 text-body-regular">
                                    Tloris je ključen dokument pri načrtovanju in oblikovanju prostora, saj arhitektom, oblikovalcem notranjosti, nepremičninskim agentom in drugim omogoča jasno predstavo o razporeditvi prostorov. Pri prodaji ali oddaji nepremičnine je tloris tudi koristen pripomoček za potencialne kupce ali najemnike, saj jim omogoča, da si lažje predstavljajo prostorsko zasnovo nepremičnine.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Katasterski načrt
                                </p>
                                <p class="mb-4 text-body-regular">
                                    Katastrski načrt je uradna zemljiška evidenca, ki vsebuje podrobne informacije o zemljiščih, parcelah in lastništvu na določenem geografskem območju. Kataster se uporablja za natančno določanje meja zemljišč, zabeležbo lastništva, določanje davčne osnove in drugih administrativnih namenov.
                                </p>
                                <p class="text-body-regular">
                                    Pomembne značilnosti katastrskega načrta vključujejo:
                                </p>
                                <ul class="mb-4 text-body-regular list-disc pl-6">
                                    <li>
                                        Parcelne meje: Prikazujejo natančne meje posameznih parcel zemljišča. Te meje so običajno določene s pomočjo koordinat, zemljepisne širine in dolžine.
                                    </li>
                                    <li>
                                        Številke parcel: Vsaka parcela na katastrskem načrtu ima svojo enolično identifikacijsko številko, ki se uporablja za prepoznavanje lastništva in druge informacije.
                                    </li>
                                    <li>
                                        Lastništvo: Kataster vsebuje informacije o lastnikih zemljišč, kar omogoča sledenje lastniškim pravicam.
                                    </li>
                                    <li>
                                        Topografske značilnosti: Prikazujejo naravne in umetne značilnosti zemljišča, kot so reke, ceste, stavbe itd.
                                    </li>
                                    <li>
                                        Administrativne meje: Katastrske karte lahko prikazujejo tudi administrativne meje, kot so občinske meje, ki so pomembne za upravljanje in organizacijo na lokalni ravni.
                                    </li>
                                </ul>
                                <p class="mb-8 text-body-regular">
                                    Katastrski načrti so ključni za vzpostavitev jasnega in zanesljivega sistema zemljiškega lastništva ter omogočajo učinkovito upravljanje zemljiških evidenc. Te informacije so pomembne za lastnike nepremičnin, kupce, oblikovalce prostora, javne organe in druge, ki delujejo v zvezi z nepremičninami.
                                </p>

                                <p class="mb-[10px] text-icon-text-medium">
                                    Izjava občine
                                </p>
                                <p class="mb-8 text-body-regular">
                                    Izjava občine pri nepremičnini je pisno potrdilo ali dokument, ki ga izda občina v zvezi z določeno nepremičnino. Ta izjava vsebuje informacije, ki so pomembne za lastništvo, namembnost, prostorsko urejanje ali druge vidike, povezane z nepremičnino. Natančne vsebine in pomen izjave občine se lahko razlikujejo glede na lokalne predpise in prakse.
                                </p>
                            </template>
                        </v-lightbox>
                    </figure>
                    <ul class="grid grid-cols-1 md:grid-cols-2 flex-col gap-x-6 gap-y-4">
                        <li 
                            v-for="(document, onOfficeEnum) in stepData.documentation_types_options" 
                            :key="'document-' + onOfficeEnum"
                        >
                            <v-checkbox
                                class="text-small-text-medium text-black-78"
                                :label="stepData.labels[onOfficeEnum].label"
                                checkbox-position="right"
                                :name="`documentation_types[${onOfficeEnum}]`"
                                :value="onOfficeEnum"
                                :checked="documentationSelected(onOfficeEnum)"
                            ></v-checkbox>
                        </li>
                    </ul>
                </figure>
            </form>
        </figure>
    </main>
    <v-add-listing-foot>
        <a
            :href="routes.previousStep ? routes.previousStep : '/'"
            class="button button--white button--md h-[48px] w-[240px] px-6 mr-4"
        >
            <v-icon icon="arrow-right" class="rotate-180" size="4"></v-icon>
            <label class="text-small-text-medium">Nazaj</label>
        </a>
      <a
        @click="submitForm('select-estate-type-step')"
        :class="['button button--md h-[48px] w-[240px] px-6', (validated ? '' : 'button button--gray')]"
      >
        <label class="text-small-text-medium">Nadaljujte</label>
        <v-icon icon="arrow-right" size="4"></v-icon>
      </a>
    </v-add-listing-foot>
  </section>
</template>
<script>
import AddListingFootVue from '../AddListingFoot.vue';
import AddListingHeadVue from '../AddListingHead.vue';
import OptionDropdown from '../OptionDropdown.vue';
import VueTailwindDatepicker from "vue-tailwind-datepicker";
import SectionSeparator from "../SectionSeparator.vue";
import FloorSelection from '../FloorSelection.vue';
import ErrorDisplay from '../ErrorDisplay.vue';

export default {
  components: {
    "v-add-listing-foot": AddListingFootVue,
    "v-add-listing-head": AddListingHeadVue,
    "v-option-dropdown": OptionDropdown,
    "v-datepicker": VueTailwindDatepicker,
    "v-separator": SectionSeparator,
    "v-floor-selection": FloorSelection,
    "v-error-display": ErrorDisplay
  },
  props: {
    old: {
      type: Object,
      required: false,
      default: {},
    },
    routes: {
      type: Object,
      required: true,
    },
    validationErrors: {
      type: Object,
      required: true,
      default: {},
    },
    stepData: {
      type: Object,
      required: true,
      default: {},
    },
  },
  data() {
    return {
        estateType: null,
        estateSubType: null,
        availability: null,
        dateFrom: null,
        dateFormatter: {
            date: 'D.M.YYYY',
            month: 'MMM'
        },
        leased: null,
        lease: null,
        numberOfBathrooms: null,
        numberOfBedrooms: null,
        numberOfRooms: null,
        numberOfFloors: null,
        landArea: null,
        livingArea: null,
        floor: null,
        constructionYear: null,
        constructionPhase: null,
        renovated: null,
        status: null,
        lastRenovationYear: null,
        windowsRenovation: null,
        floorsRenovation: null,
        roofRenovation: null,
        wallsRenovation: null,
        heatingRenovation: null,
        plumbingRenovation: null,
        electricalRenovations: null,
        estateEquipment: null,
        isMonument: null,
        allowsPets: null,
        mainHeating: [],
        bistvenoOgrevanje: [],
        energyCertificateType: null,
        energyConsumption: null,
        hotWaterIncludedInCalculations: null,
        energyClass: 'A',
        selectedEnergyClass: 1, // selected value
        energyCertificateValidUntil: null,
        utilityConnections: null,
        buildability: null,
        extraData: [],
        parkingSpaces: [],
        documentation_types: [],
    };
  },
  mounted() {
    this.estateType = this.stepData.objektart;
    this.estateSubType = (this.old.objekttyp ? this.old.objekttyp : this.stepData.objekttyp);
    this.availability = (this.old.verfuegbar_ab ? this.old.verfuegbar_ab : this.stepData.verfuegbar_ab);
    this.dateFrom = (this.old.abdatum ? this.old.abdatum : (this.stepData.abdatum ? this.stepData.abdatum : {startDate : null}));
    this.leased = this.old.vermietet
            ? this.old.vermietet
            : this.stepData.vermietet != null
            ? Number(this.stepData.vermietet)
            : null;
    this.lease = this.old.mieteinnahmen_pro_monat
        ? this.old.mieteinnahmen_pro_monat
        : this.stepData.mieteinnahmen_pro_monat;

    this.numberOfBathrooms = (this.old.anzahl_badezimmer ? this.old.anzahl_badezimmer : this.stepData.anzahl_badezimmer)
    this.numberOfBedrooms = (this.old.anzahl_schlafzimmer ? this.old.anzahl_schlafzimmer : this.stepData.anzahl_schlafzimmer)
    this.numberOfRooms= (this.old.anzahl_zimmer ? this.old.anzahl_zimmer : this.stepData.anzahl_zimmer)
    this.numberOfFloors = (this.old.etagen_zahl ? this.old.etagen_zahl : this.stepData.etagen_zahl)
    this.landArea = (this.old.grundstuecksflaeche ? this.old.grundstuecksflaeche : this.stepData.grundstuecksflaeche)
    this.livingArea = (this.old.wohnflaeche ? this.old.wohnflaeche : this.stepData.wohnflaeche)
    this.floor = (this.old.etage ? this.old.etage : this.stepData.etage)
    this.constructionYear = (this.old.baujahr ? this.old.baujahr : this.stepData.baujahr)
    this.constructionPhase = (this.old.bauphase ? this.old.bauphase : this.stepData.bauphase)

    this.renovated = (this.old.renovation_status ? this.old.renovation_status : (this.stepData.renovation_status != null ? Number(this.stepData.renovation_status) : null))
    this.status = (this.old.zustand ? this.old.zustand : this.stepData.zustand);
    this.lastRenovationYear = this.old.last_renovation_year ? this.old.last_renovation_year : this.stepData.last_renovation_year;
    this.windowsRenovation = this.old.renovation_window ? this.old.renovation_window : this.stepData.renovation_window;
    this.floorsRenovation = this.old.renovation_floor ? this.old.renovation_floor : this.stepData.renovation_floor;
    this.roofRenovation = this.old.renovation_roof ? this.old.renovation_roof : this.stepData.renovation_roof;
    this.wallsRenovation = this.old.renovation_walls ? this.old.renovation_walls : this.stepData.renovation_walls;
    this.heatingRenovation = this.old.heatingRenovation ? this.old.heatingRenovation : this.stepData.renovation_heating;
    this.plumbingRenovation = this.old.plumbingRenovation ? this.old.plumbingRenovation : this.stepData.renovation_pipeing;
    this.electricalRenovations = this.old.electricalRenovations ? this.old.electricalRenovations : this.stepData.renovation_electrical;
    this.estateEquipment = this.old.estateEquipment ? this.old.estateEquipment : this.stepData.ind_2028_Feld_ObjAusstattung39
    this.isMonument = this.old.denkmalgeschuetzt ? this.old.denkmalgeschuetzt : this.stepData.denkmalgeschuetzt;
    this.allowsPets = this.old.haustiere ? this.old.haustiere : this.stepData.haustiere;

    this.mainHeating = this.old.befeuerung ? Object.values(this.old.befeuerung) : (this.stepData.befeuerung ? Object.values(this.stepData.befeuerung) : [])
    this.bistvenoOgrevanje = this.old.heizungsart ? Object.values(this.old.heizungsart) : (this.stepData.heizungsart ? Object.values(this.stepData.heizungsart) : [])

    this.energyCertificateType = this.old.energieausweistyp
        ? this.old.energieausweistyp
        : this.stepData.energieausweistyp;
    this.energyConsumption = this.old.energieverbrauchskennwert
        ? this.old.energieverbrauchskennwert
        : this.stepData.energieverbrauchskennwert;
    this.hotWaterIncludedInCalculations = this.old.warmwasserEnthalten ? this.old.warmwasserEnthalten : this.stepData.warmwasserEnthalten;
    this.energyClass = this.old.energyClass
        ? this.old.energyClass
        : (this.stepData.energyClass ?? 'A');

    if (this.stepData.energyClass != null) {
        this.selectedEnergyClass = this.mapEnergyClass(
            null,
            this.stepData.energyClass
        );
    }
    if (this.old.energyClass != null) {
        this.selectedEnergyClass = this.mapEnergyClass(
            null,
            this.old.energyClass
        );
    }

    this.energyCertificateValidUntil = this.old.energieausweis_gueltig_bis
        ? this.old.energieausweis_gueltig_bis
        : this.stepData.energieausweis_gueltig_bis
        ? this.stepData.energieausweis_gueltig_bis
        : "";

    this.utilityConnections = (this.old.erschliessung ? this.old.erschliessung : (this.stepData.erschliessung ? this.stepData.erschliessung : null));
    this.buildability = (this.old.bebaubar_nach ? this.old.bebaubar_nach : (this.stepData.bebaubar_nach ? this.stepData.bebaubar_nach : null));
    this.extraData = this.old.extra_data ? Object.values(this.old.extra_data) : (this.stepData.extra_data ? Object.values(this.stepData.extra_data) : [])
    this.parkingSpaces = this.old.stellplatzart ? Object.values(this.old.stellplatzart) : (this.stepData.stellplatzart ? Object.values(this.stepData.stellplatzart) : [])
    this.documentation_types = this.old.documentation_types ? Object.values(this.old.documentation_types) : (this.stepData.documentation_types ? Object.values(this.stepData.documentation_types) : [])
    // if (this.old.extra_data) {
    //     this.extraData = this.old.extra_data;
    // } else {
    //     for (const [option, sth] in this.stepData.extra_data) {
    //         this.extraData[option] = "1";
    //     }
    // }

    // if (this.old.stellplatzart) {
    //     this.parkingSpaces = this.old.stellplatzart;
    // } else {
    //     for (const [option, sth] in this.stepData.stellplatzart) {
    //         this.extraData[option] = "1";
    //     }
    // }
  },
  methods: {
    onFloorChanged(newFloor) {
        this.floor = newFloor
    },
    extraDataSelected(key) {
        return this.extraData.includes(key);
    },
    parkingSelected(key) {
        return this.parkingSpaces.includes(key);
    },
    mainHeatingSelected(key) {
        return this.mainHeating.includes(key);
    },
    documentationSelected(key) {
        return this.documentation_types.includes(key);
    },
    bistvenoOgrevanjeSelected(key) {
        return this.bistvenoOgrevanje.includes(key);
    },
    submitForm(formId) {
      if(this.validated) document.getElementById(formId).submit();
    },
    checkboxChanged(value) {
        if(this.mainHeating.includes(value)) {
            this.mainHeating = this.mainHeating.filter(val => val !== value)
        } else {
            this.mainHeating.push(value)
        }
    },
    clearDate() {
      this.$refs.datumKdajBoVseljivoRef.clearPicker()
    },
    showFloor() {
        let validOptions = ["wohnung", "ind_Schl_3259", "zimmer"];
        return validOptions.includes(this.stepData.objektart);
    },
    onEstateSubypeChange(newValue) {
        this.estateSubType = newValue;
    },
    onUtilityConnectionChange(newValue) {
        this.utilityConnections = newValue;
    },
    onBuildabilityChange(newValue) {
        this.buildability = newValue;
    },
    onLeasedChange(newValue) {
        this.leased = newValue;
    },
    onIsRenovatedChange(newValue) {
        if (this.renovated !== newValue) {
            this.renovated = newValue;
            this.status = null;
        }

        if (!this.renovated) {
            this.lastRenovationYear = null;
            this.windowsRenovation = null;
            this.floorsRenovation = null;
            this.roofRenovation = null;
            this.wallsRenovation = null;
            this.heatingRenovation = null;
            this.plumbingRenovation = null;
            this.electricalRenovations = null;
        }
    },
    onRenovatedYesNoChange(newValue) {
        this.status = newValue;
    },
    onEstateEquipmentChange(newValue) {
        this.estateEquipment = newValue;
    },
    prerequisitesMet(section)
    {
        if (section == 'subtype') {
            if (this.estateType == null || this.estateType == 'zimmer') {
                return false;
            }
        }

        if (section == 'vseljivost') {
            if (this.estateType != 'zimmer' && (this.estateSubType == null || this.estateSubType == '')) {
                return false;
            }
        }

        if (section == 'leased') {
            if (this.availability == null || !this.showLeasedStatus) {
                return false;
            }
        }

        if (section == 'size') {
            if (this.availability == null) {
                return false;
            }

            if (this.showLeasedStatus) {
                if (this.leased == null || (this.leased == 1 && !this.lease)) {
                    return false;
                }
            }
        }

        if (section == 'floor') {
            if (!this.showFloor()) {
                return false;
            }

            return this.sizeDataValidated;
        }

        if (section == 'status-gradnje') {
            if (!this.showConstructionStatus) {
                return false;
            }

            if (this.showFloor()) {
                return this.floor !== null;
            }

            return this.sizeDataValidated;
        }

        if (section == 'opremljenost') {
            if (!this.showExtraData) {
                return false;
            }

            if (this.showConstructionStatus) {
                if (this.constructionPhase == 'HausFertigGestellt') {
                    return this.constructionYear != null;
                }
                return this.constructionPhase != null;
            }

            if (this.showFloor()) {
                return this.floor !== null;
            }

            return this.sizeDataValidated;
        }

        if (section == 'renovations') {
            if (!this.showRenovations) {
                return false;
            }

            if (this.showExtraData) {
                return this.estateEquipment != null;
            }

            if (this.showFloor()) {
                return this.floor !== null;
            }

            if (this.showConstructionStatus) {
                return this.constructionPhase != null;
            }

            return this.sizeDataValidated;
        }

        if (section == 'renovated-what')
        {
            if (!this.prerequisitesMet('renovations')) {
                return false;
            }

            if (!this.renovated) {
                return false;
            }

            return this.status != null;
        }

        if (section == 'utility-connections')
        {
            // Visible only for parcela, so no need to check anything else but size
            if (!this.showConnectionsBuildability) {
                return false;
            }

            return this.sizeDataValidated;
        }

        if (section == 'buildability')
        {
            if (!this.showConnectionsBuildability) {
                return false;
            }

            return this.utilityConnections != null;
        }

        if (section == 'extra-data') {
            if (!this.showExtraData) {
                return false;
            }

            if (this.showRenovations) {
                if (this.prerequisitesMet('renovated-what')) {
                    return this.lastRenovationYear != null;
                }
                if (this.prerequisitesMet('renovations')) {
                    return this.status != null;
                }

                return false;
            }

            if (this.estateEquipment == null) {
                return false;
            }

            if (this.showConstructionStatus) {
                return this.constructionPhase != null;
            }

            return this.sizeDataValidated;
        }

        if (section == 'heating')
        {
            if (!this.showHeating) {
                return false;
            }

            if (this.prerequisitesMet('extra-data')) {
                return true;
            }

            if (this.showRenovations) {
                if (this.prerequisitesMet('renovated-what')) {
                    return this.lastRenovationYear != null;
                }
                if (this.prerequisitesMet('renovations')) {
                    return this.status != null;
                }

                return false;
            }

            if (this.showExtraData) {
                return this.estateEquipment != null;
            }

            if (this.showConstructionStatus) {
                return this.constructionPhase != null;
            }

            return this.sizeDataValidated;
        }

        if (section == 'documentation')
        {
            if (!this.showDocumentation) {
                return false;
            }

            if (this.prerequisitesMet('heating')) {
                if (this.energyCertificateType == null) return false;

                if (this.energyCertificateType == "Endenergiebedarf") {
                    if (
                        !this.energyConsumption ||
                        !this.energyCertificateValidUntil
                    )
                        return false;
                }
            }

            if (this.showConnectionsBuildability) {
                return this.buildability != null;
            }

            if (this.showRenovations) {
                if (this.prerequisitesMet('renovated-what')) {
                    return this.lastRenovationYear != null;
                }
                if (this.prerequisitesMet('renovations')) {
                    return this.status != null;
                }

                return false;
            }

            if (this.showExtraData) {
                return this.estateEquipment != null;
            }

            if (this.showConstructionStatus) {
                return this.constructionPhase != null;
            }

            return this.sizeDataValidated;
        }

        return true;
    },
    mapEnergyClass(integer = null, char = null) {
        /*
            * int | label | onOffice
            *  1 = A1 = A
            *  2 = A2 = A+
            *  3 = B1 = B
            *  4 = B2 = ind_Schl_3153
            *  5 = C = C
            *  6 = D = D
            *  7 = E = E
            *  8 = F = F
            *  9 = G = G
            */

        if (integer == 1) return "A";
        if (integer == 2) return "A+";
        if (integer == 3) return "B";
        if (integer == 4) return "ind_Schl_3153";
        if (integer == 5) return "C";
        if (integer == 6) return "D";
        if (integer == 7) return "E";
        if (integer == 8) return "F";
        if (integer == 9) return "G";

        if (char == "A") return 1;
        if (char == "A+") return 2;
        if (char == "B") return 3;
        if (char == "ind_Schl_3153") return 4;
        if (char == "C") return 5;
        if (char == "D") return 6;
        if (char == "E") return 7;
        if (char == "F") return 8;
        if (char == "G") return 9;
    },
  },
  watch: {
    dateFrom: {
        handler(newValue, oldValue) {
            if (!newValue || newValue.startDate == null) {
                return;
            }
            this.availability = "later";
        }
    },
    constructionPhase(newValue, oldValue) {
        if (oldValue != null) {
            this.constructionYear = null;
        }
    },
    selectedEnergyClass: {
            handler(newValue) {
                if (newValue != null) {
                    this.energyClass = this.mapEnergyClass(newValue, null);
                }
            },
        },
  },
  computed: {
    floorDefaultValue() {
        return (this.old.etage ? this.old.etage : (this.stepData.etage ?? 1))
    },
    objekttypDefaultValue() {
        return this.old.objekttyp ? this.old.objekttyp : this.stepData.objekttyp;
    },
    estateEquipmentDefaultValue() {
        return this.old.ind_2028_Feld_ObjAusstattung39 ? this.old.ind_2028_Feld_ObjAusstattung39 : this.stepData.ind_2028_Feld_ObjAusstattung39;
    },
    warmWaterCasted() {
        return this.hotWaterIncludedInCalculations ? 1 : 0;
    },
    energyClassIndexComputed() {
        this.energyClass = this.mapEnergyClass(
            this.selectedEnergyClass,
            null
        );
    },
    showDocumentation() {
        if (this.stepData.vermarktungsart == 'miete') {
            return false;
        }

        return true;
    },
    showHeating() {
        return !(["grundstueck", "zimmer"].includes(this.estateType));
    },
    showRenovations() {
        return !(["ind_Schl_3259", "hausbau", "grundstueck"].includes(this.estateType));
    },
    showExtraData() {
        return this.estateType != 'grundstueck';
    },
    showConnectionsBuildability() {
        return this.estateType == 'grundstueck';
    },
    isRenovatedDefaultValue() {
        return (this.old.renovation_status ? this.old.renovation_status : (this.stepData.renovation_status != null ? Number(this.stepData.renovation_status) : null));
    },
    isRenovatedOptions() {
        return [
            {label: 'Da', value: 1},
            {label: 'Ne', value: 0},
        ]
    },
    renovatedYesOptions() {
        return [
            {label: 'Delna prenova', value: 'modernisiert'},
            {label: 'Celostna sanacija', value: 'saniert'},
        ];
    },
    renovatedNoOptions() {
        return [
            {label: 'Prva vselitev', value: 'erstbezug_nach_sanierung'},
            {label: 'Kot nova', value: 'neuwertig'},
            {label: 'Dobro ohranjena', value: 'gepflegt'},
            {label: 'Potrebuje prenovo', value: 'renovierungsbeduerftig'},
        ];
    },
    estateEquipmentOptions() {
        return [
            {label: 'Neopremljeno', value: 'ind_Schl_3131'},
            {label: 'Delno opremljeno', value: 'ind_Schl_3133'},
            {label: 'Opremljeno', value: 'ind_Schl_3135'}
        ]
    },
    utilityConnectionOptions() {
        return [
            {label: 'Priključena v celoti', value: 'erschlossen'},
            {label: 'Delno priključena', value: 'teilerschlossen'},
            {label: 'Ni priključena', value: 'unerschlossen'}
        ]
    },
    renovatedCasted() {
      return this.renovated ? 1 : 0;
    },
    windowsRenovationCasted() {
        return this.windowsRenovation ? 1 : 0;
    },
    floorsRenovationCasted() {
        return this.floorsRenovation ? 1 : 0;
    },
    roofRenovationCasted() {
        return this.roofRenovation ? 1 : 0;
    },
    wallsRenovationCasted() {
        return this.wallsRenovation ? 1 : 0;
    },
    heatingRenovationCasted() {
        return this.heatingRenovation ? 1 : 0;
    },
    plumbingRenovationCasted() {
        return this.plumbingRenovation ? 1 : 0;
    },
    electricalRenovationsCasted() {
        return this.electricalRenovations ? 1 : 0;
    },
    isMonumentCasted() {
        return this.isMonument ? 1 : 0;
    },
    showRenovations() {
        return !(["ind_Schl_3259", "hausbau", "grundstueck"].includes(this.estateType));
    },
    showConstructionStatus() {
        return ["ind_Schl_3259", "wohnung", "haus", "hausbau"].includes(this.estateType);
    },
    estateTypeOptions(){
        return this.stepData.objekttyp_options[this.estateType].map((option) => { return {label: option.label, value: option.enum}})
    },
    buildabilityOptions(){
        let options = [];
        for (const [ooEnum, desc] of Object.entries(this.stepData.bebaubar_nach_options)) {
            options.push({label: desc, value: ooEnum});
        }
        return options;
    },
    leasedOptions() {
        return [
            {'label': 'Da', 'value': 1},
            {'label': 'Ne', 'value': 0}
        ];
    },
    leaseTypeCasted() {
        return this.leaseType ? 1 : 0;
    },
    showLeasedStatus() {
        if (this.stepData.vermarktungsart == 'miete') {
            return false;
        }

        if (["ind_Schl_3259", "hausbau", "grundstueck"].includes(this.estateType)) {
            return false;
        }

        return true;
    },
    leasedDefaultValue() {
        return this.old.vermietet
            ? this.old.vermietet
            : this.stepData.vermietet != null
            ? Number(this.stepData.vermietet)
            : null;
    },
    sizeDataValidated() {
        if(this.stepData.objektart == 'haus' || this.stepData.objektart == "hausbau") {
            if(
            !this.numberOfBathrooms ||
            !this.numberOfBedrooms ||
            !this.numberOfRooms ||
            !this.numberOfFloors ||
            !this.landArea ||
            !this.livingArea
            ) return false
        }

        if(this.stepData.objektart == 'zimmer') {
            if(!this.livingArea) return false
        }

        if(this.stepData.objektart == 'wohnung' || this.stepData.objektart == "ind_Schl_3259") {
            if(
            !this.livingArea ||
            !this.numberOfRooms ||
            !this.numberOfBedrooms ||
            !this.numberOfBathrooms
            ) return false
        }

        if(this.stepData.objektart == 'grundstueck') {
            if(!this.landArea) return false
        }

        return true;
    },
    validated() {
        if(this.estateType != 'zimmer' && !this.estateSubType) {
            return false;
        }

        if(this.availability == 'later' && !this.dateFrom) {
            return false;
        }
        if(!this.availability) {
            return false;
        }
        if (this.showLeasedStatus) {
            if (this.leased == null) return false;

            if (this.leased == 1 && !this.lease) return false;
        }

        if (!this.sizeDataValidated) {
            return false;
        }

        if (this.showConstructionStatus) {
            if(!this.constructionPhase) return false

            if(this.constructionPhase == 'HausFertigGestellt') {
                if(!this.constructionYear) return false
            }
        }

        if(this.showExtraData && !this.estateEquipment) {
            return false;
        }

        if (this.showRenovations) {
            if(this.renovated === null || this.status == null) {
                return false;
            }

            if(this.renovated == 1 && (this.lastRenovationYear == null || this.lastRenovationYear == '')) {
                return false;
            }
        }

        if (
            this.stepData.vermarktungsart == "miete" &&
            this.stepData.showpets &&
            this.allowsPets === null
        ) {
            return false;
        }

        if (this.showHeating) {
            if(this.mainHeating.length == 0) {
                return false;
            }

            if (this.energyCertificateType == null) return false;

            if (this.energyCertificateType == "Endenergiebedarf") {
                if (
                    !this.energyConsumption ||
                    !this.energyCertificateValidUntil
                )
                    return false;
            }
        }

        if (this.showConnectionsBuildability) {
            if (this.utilityConnections == null) return false;
            if (this.buildability == null) return false;
        }

      return true;
    }
  }
};
</script>
